import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GENDERS, ORIENTATIONS, GOALS, KINKS } from "@/constants";
import { tierOf } from "@/lib/membership";
import { toast } from "sonner";
import { Upload, BadgeCheck, MapPin, EyeOff, Lock, ShieldCheck, LifeBuoy } from "lucide-react";
import { AddressEditor } from "@/components/VellutoAddress";
import { ProfileNudgeBanner } from "@/components/ProfileNudgeBanner";
import { AccountSecurity } from "@/components/AccountSecurity";
import { SocialFields } from "@/lib/socials";

const CONSENT = [
  "I only connect with consenting adults (18+)",
  "I respect stated boundaries and safewords",
  "I keep private photos and details discreet",
  "I communicate limits before any arrangement",
];

export default function EditProfile() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [idVerifying, setIdVerifying] = useState(false);
  const fileRef = useRef(null);
  const selfieRef = useRef(null);
  const initialLoc = useRef("");
  const geoUsed = useRef(false);

  useEffect(() => { if (user) { setF({ ...user }); initialLoc.current = user.location || ""; } }, [user]);

  // Returning from the Stripe Identity hosted flow — poll for the result.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("identity") !== "submitted") return;
    let tries = 0;
    toast("Checking your verification…");
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get("/identity/status");
        if (data.age_verified) {
          clearInterval(poll);
          await refresh();
          toast.success("You're verified — 18+ confirmed");
          window.history.replaceState({}, "", "/me");
        } else if (data.status === "requires_input" || tries >= 6) {
          clearInterval(poll);
          if (tries >= 6 && data.status !== "requires_input")
            toast("Verification is still processing — check back shortly.");
          else
            toast.error("Verification could not be completed. Please try again.");
          window.history.replaceState({}, "", "/me");
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(poll);
  }, [refresh]);

  const startAgeVerify = async () => {
    setIdVerifying(true);
    try {
      const { data } = await api.post("/identity/session");
      if (data.already_verified) { await refresh(); toast.success("You're already verified"); return; }
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start verification");
      setIdVerifying(false);
    }
  };

  if (!f) return <div className="pt-32 text-center text-text-muted">Loading…</div>;

  const set = (k, v) => setF({ ...f, [k]: v });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd);
      set("photo_url", data.photo_url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onVerify = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVerifying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/verify/selfie", fd);
      set("verification_status", "pending");
      await refresh();
      toast.success("Selfie submitted — we'll review it shortly");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };
  const toggle = (k, v) => {
    const arr = f[k] || [];
    set(k, arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };
  const toggleConsent = (c) => {
    const cc = { ...(f.consent_checklist || {}) };
    cc[c] = !cc[c];
    set("consent_checklist", cc);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    toast("Requesting your location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => { geoUsed.current = true; setF((prev) => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude })); },
      () => toast.error("Couldn't get your location"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const save = async () => {
    const locationChanged = (f.location || "") !== initialLoc.current;
    const payload = {
      display_name: f.display_name, age: parseInt(f.age, 10), tagline: f.tagline, bio: f.bio,
      location: f.location, gender: f.gender, orientation: f.orientation,
      relationship_goals: f.relationship_goals, kinks: f.kinks, photo_url: f.photo_url,
      discreet_mode: f.discreet_mode, consent_checklist: f.consent_checklist,
      incognito: !!f.incognito, ...(user?.is_creator ? { socials: f.socials || {}, public_socials: (f.public_socials || []).filter((k) => f.socials?.[k]) } : {}),
    };
    // Only send coordinates when they come from GPS or the city text is unchanged;
    // if the user typed a new city, let the backend geocode it.
    if (geoUsed.current || !locationChanged) {
      payload.lat = f.lat;
      payload.lng = f.lng;
    }
    await api.put("/profile", payload);
    await refresh();
    initialLoc.current = f.location || "";
    geoUsed.current = false;
    toast.success("Profile saved");
  };

  const field = "w-full bg-transparent border-b border-white/15 py-3 focus:border-wine outline-none transition-colors";
  const label = "text-xs uppercase tracking-[0.2em] text-text-muted mb-3 block";
  const isPrem = tierOf(user) === "premium";
  const chip = (val, arr, k) => (
    <button key={val} data-testid={`chip-${k}-${val}`} onClick={() => toggle(k, val)}
      className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors ${
        (arr || []).includes(val) ? "border-wine bg-wine/20 text-text-main" : "border-white/10 text-text-muted hover:border-white/30"}`}>
      {val}
    </button>
  );

  return (
    <div className="pt-24 pb-24 max-w-3xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Your profile</p>
        <h1 className="font-display text-6xl font-light mb-8">Compose yourself</h1>
        <ProfileNudgeBanner onProfilePage />

        <div className="mb-10 max-w-lg">
          <span className={label}>Your Velluto address</span>
          <AddressEditor handle={user?.handle} onChanged={() => refresh()} />
          <p className="text-[11px] text-text-muted mt-2">Members can type this into "Find a creator" to reach you directly. Share it anywhere.</p>
        </div>

        <div className="space-y-10">
          <div className="grid sm:grid-cols-2 gap-6">
            <div><span className={label}>Display name</span><input data-testid="edit-name" className={field} value={f.display_name || ""} onChange={(e) => set("display_name", e.target.value)} /></div>
            <div><span className={label}>Age</span><input data-testid="edit-age" type="number" min="18" className={field} value={f.age || ""} onChange={(e) => set("age", e.target.value)} /></div>
          </div>
          <div>
            <span className={label}>Photo</span>
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-2xl overflow-hidden border border-white/10 bg-surface shrink-0">
                {f.photo_url
                  ? <img src={f.photo_url} alt="" className={`w-full h-full object-cover ${f.discreet_mode ? "blur-md" : ""}`} />
                  : <div className="w-full h-full flex items-center justify-center text-text-muted text-xs">No photo</div>}
              </div>
              <div className="flex-1">
                <input ref={fileRef} data-testid="photo-file" type="file" accept="image/*" onChange={onUpload} className="hidden" />
                <button type="button" data-testid="photo-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-2 px-6 py-3 border border-white/15 rounded-full text-sm hover:border-wine transition-colors disabled:opacity-50">
                  <Upload size={15} /> {uploading ? "Uploading…" : "Upload a photo"}
                </button>
                <p className="text-xs text-text-muted mt-2">JPG, PNG, WEBP or GIF · up to 8MB. Blurred by discreet mode.</p>
              </div>
            </div>
          </div>
          <div><span className={label}>…or paste an Unsplash or Pexels image link</span><input data-testid="edit-photo" className={field} value={f.photo_url || ""} onChange={(e) => set("photo_url", e.target.value)} placeholder="https://images.unsplash.com/…" /></div>

          <div className="border border-white/8 rounded-2xl p-6 bg-surface/60">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 text-sm">
                  <BadgeCheck size={16} className={f.verified ? "text-emerald-400" : "text-text-muted"} />
                  {f.verified ? "You're verified" : f.verification_status === "pending" ? "Verification under review" : f.verification_status === "rejected" ? "Verification not approved" : "Get verified"}
                </p>
                <p className="text-xs text-text-muted mt-1 max-w-sm">
                  {f.verification_status === "pending"
                    ? "Our team is reviewing your selfie. You'll get the badge once it's approved — usually within a day."
                    : f.verification_status === "rejected"
                    ? "That selfie couldn't be approved. Take a clear, well-lit selfie of your face and try again."
                    : "Take a quick selfie for our team to review. Approved members earn a Verified badge. Your selfie stays private and is never shown."}
                </p>
              </div>
              {f.verified ? (
                <span data-testid="verified-badge" className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/40 text-emerald-300 text-xs uppercase tracking-[0.15em]">
                  <BadgeCheck size={15} /> Verified
                </span>
              ) : f.verification_status === "pending" ? (
                <span data-testid="verify-pending" className="shrink-0 px-5 py-2.5 rounded-full border border-amber-500/40 text-amber-300 text-xs uppercase tracking-[0.15em]">
                  Under review
                </span>
              ) : (
                <>
                  <input ref={selfieRef} data-testid="selfie-file" type="file" accept="image/*" capture="user" onChange={onVerify} className="hidden" />
                  <button type="button" data-testid="verify-btn" onClick={() => selfieRef.current?.click()} disabled={verifying}
                    className="shrink-0 flex items-center gap-2 px-6 py-3 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors text-sm disabled:opacity-50">
                    <BadgeCheck size={15} /> {verifying ? "Submitting…" : f.verification_status === "rejected" ? "Try again" : "Verify with a selfie"}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="border border-white/8 rounded-2xl p-6 bg-surface/60" data-testid="age-verify-card">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 text-sm">
                  <ShieldCheck size={16} className={user?.age_verified ? "text-gold" : "text-text-muted"} />
                  {user?.age_verified ? "Age verified — 18+" : "Verify your age (18+)"}
                </p>
                <p className="text-xs text-text-muted mt-1 max-w-sm">
                  {user?.age_verified
                    ? "Your government ID and selfie confirmed you're an adult. This unlocks full trust across Velluto."
                    : "Confirm you're 18+ with a real government ID and a matching selfie, powered by Stripe Identity. Secure and private — we never store your documents."}
                </p>
              </div>
              {user?.age_verified ? (
                <span data-testid="age-verified-badge" className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/40 text-gold text-xs uppercase tracking-[0.15em]">
                  <ShieldCheck size={15} /> 18+ Verified
                </span>
              ) : (
                <button type="button" data-testid="age-verify-btn" onClick={startAgeVerify} disabled={idVerifying}
                  className="shrink-0 flex items-center gap-2 px-6 py-3 rounded-full border border-gold/40 text-gold hover:bg-gold/10 transition-colors text-sm disabled:opacity-50">
                  <ShieldCheck size={15} /> {idVerifying ? "Opening…" : "Verify I'm 18+"}
                </button>
              )}
            </div>
          </div>

          <div>
            <span className={label}>City</span>
            <input data-testid="edit-location" className={field} value={f.location || ""} onChange={(e) => set("location", e.target.value)} />
            <button type="button" data-testid="use-location" onClick={useMyLocation}
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border border-white/15 hover:border-wine transition-colors text-text-muted">
              <MapPin size={14} /> {f.lat != null ? "Location captured — update" : "Use my location for distance"}
            </button>
          </div>
          <div><span className={label}>Bio</span><textarea data-testid="edit-bio" rows={3} className={`${field} resize-none`} value={f.bio || ""} onChange={(e) => set("bio", e.target.value)} /></div>

          <div><span className={label}>Gender</span><div className="flex flex-wrap gap-2">{GENDERS.map((g) => (
            <button key={g} data-testid={`gender-${g}`} onClick={() => set("gender", g)} className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors ${f.gender === g ? "border-wine bg-wine/20" : "border-white/10 text-text-muted hover:border-white/30"}`}>{g}</button>
          ))}</div></div>

          <div><span className={label}>Orientation</span><div className="flex flex-wrap gap-2">{ORIENTATIONS.map((o) => (
            <button key={o} data-testid={`orient-${o}`} onClick={() => set("orientation", o)} className={`text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors ${f.orientation === o ? "border-wine bg-wine/20" : "border-white/10 text-text-muted hover:border-white/30"}`}>{o}</button>
          ))}</div></div>

          <div><span className={label}>Looking for</span><div className="flex flex-wrap gap-2">{GOALS.map((g) => chip(g, f.relationship_goals, "relationship_goals"))}</div></div>
          <div><span className={label}>Interests & kinks</span><div className="flex flex-wrap gap-2">{KINKS.map((k) => chip(k, f.kinks, "kinks"))}</div></div>

          <div className="flex items-center justify-between border-y border-white/8 py-5">
            <div><p className="text-sm">Discreet mode</p><p className="text-xs text-text-muted mt-1">Blur your photo until you choose to reveal</p></div>
            <button data-testid="edit-discreet" onClick={() => set("discreet_mode", !f.discreet_mode)}
              className={`w-14 h-7 rounded-full transition-colors relative ${f.discreet_mode ? "bg-wine" : "bg-white/15"}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${f.discreet_mode ? "left-8" : "left-1"}`} />
            </button>
          </div>

          <div className="flex items-center justify-between border-b border-white/8 pb-5">
            <div>
              <p className="text-sm flex items-center gap-2"><EyeOff size={15} className="text-gold" /> Incognito mode
                {!isPrem && <span className="text-[9px] uppercase tracking-[0.15em] text-gold border border-gold/40 px-2 py-0.5 rounded-full">Premium</span>}
              </p>
              <p className="text-xs text-text-muted mt-1">Browse invisibly — you're hidden from Discover and leave no trace</p>
            </div>
            {isPrem ? (
              <button data-testid="edit-incognito" onClick={() => set("incognito", !f.incognito)}
                className={`w-14 h-7 rounded-full transition-colors relative ${f.incognito ? "bg-gold" : "bg-white/15"}`}>
                <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${f.incognito ? "left-8" : "left-1"}`} />
              </button>
            ) : (
              <a href="/premium" data-testid="incognito-upgrade" className="shrink-0 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-gold border border-gold/40 px-4 py-2 rounded-full hover:bg-gold/10 transition-colors">
                <Lock size={12} /> Upgrade
              </a>
            )}
          </div>

          <div>
            <span className={label}>Consent charter</span>
            <div className="space-y-3">
              {CONSENT.map((c) => (
                <button key={c} data-testid={`consent-${c.slice(0,10)}`} onClick={() => toggleConsent(c)} className="flex items-start gap-3 text-left w-full group">
                  <span className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-colors ${f.consent_checklist?.[c] ? "border-wine bg-wine" : "border-white/20"}`}>
                    {f.consent_checklist?.[c] && <span className="text-xs">✓</span>}
                  </span>
                  <span className="text-sm text-text-muted group-hover:text-text-main transition-colors">{c}</span>
                </button>
              ))}
            </div>
          </div>

          {user?.is_creator && <div data-testid="edit-socials-section">
            <span className={label}>Your socials</span>
            <p className="text-xs text-text-muted -mt-1 mb-4">Private by default — only the Velluto team sees them. Tap <span className="text-gold">Public</span> on any handle to show it on your profile to verified members.</p>
            <SocialFields prefix="edit-social" value={f.socials || {}} onChange={(v) => set("socials", v)}
              publicKeys={f.public_socials || []} onPublicChange={(v) => set("public_socials", v)} />
          </div>}

          <button data-testid="save-profile" onClick={save} className="w-full py-4 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.2em]">Save profile</button>

          <a href="/support" data-testid="contact-support-link" className="flex items-center justify-between gap-4 border border-white/8 rounded-2xl p-5 bg-surface/60 hover:border-wine/40 transition-colors">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-wine/15 text-gold flex items-center justify-center"><LifeBuoy size={17} /></span>
              <div><p className="text-sm">Need a hand?</p><p className="text-xs text-text-muted mt-0.5">Contact the Velluto support team</p></div>
            </div>
            <span className="text-xs uppercase tracking-[0.15em] text-gold">Get help →</span>
          </a>
        </div>
        <AccountSecurity />
      </motion.div>
    </div>
  );
}
